/**
 * Конечная точка указателя (docs/wayfinding/plan.md §9.1.7).
 *
 * Указатель ведёт к объекту — чаще всего табличка на двери ведёт к своему помещению. Связь
 * одна на указатель: выбранный объект наполняет связанные поля макета ВСЕХ сторон, каждое из
 * своего столбца таблицы объектов (связи настраиваются у типа, §9.1.6). В поля ложатся живые
 * ссылки (`autocraftObjectRef`), поэтому переименование помещения долетает до макета само.
 *
 * Правила — здесь, а не в панели: ту же раскладку по сторонам читают карта (пунктир связи),
 * дубль карты и копия на другой план (перепривязка id).
 */
import {
  AUTOCRAFT_COVER_PROPERTY,
  AUTOCRAFT_COVER_SIGNATURE_OUTDATED,
  AUTOCRAFT_COVER_SIGNATURE_PROPERTY,
  AUTOCRAFT_DATA_PROPERTY,
  AUTOCRAFT_DEFAULT_PREVIEW_PAGE,
  AUTOCRAFT_GENERATOR_PROPERTY,
  AUTOCRAFT_PAGE_COUNT_PROPERTY,
  AUTOCRAFT_PAGE_PROPERTY,
  AUTOCRAFT_PDF_PROPERTY,
} from '../types/autocraft.js';
import {
  WAYFINDING_SIGN_DESTINATION_PROPERTY,
  WAYFINDING_SIGN_MAX_SIDES,
} from '../types/wayfinding.js';
import { autocraftObjectRef, readAutocraftObjectRef } from './autocraftObjectRefs.js';
import { holdsForeignAutocraftData } from './wayfindingAutocraftAdoption.js';
import { stringifyPropertyValue } from './stringifyPropertyValue.js';
import {
  readWayfindingSignSideProperties,
  toWayfindingSignSidePatch,
  wayfindingSignSideKey,
} from './wayfindingSignSides.js';

function readData(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Конечная точка указателя; `null` — не выбрана.
 *
 * Только из своего ключа: выводить точку из ссылок полей нельзя — ссылки лежат и в данных
 * сторон, которых у типа уже нет, и в данных прежнего генератора, и снять такую «точку» было бы
 * нечем.
 */
export function readWayfindingSignDestination(
  properties: Record<string, unknown> | null | undefined,
): string | null {
  const own = properties?.[WAYFINDING_SIGN_DESTINATION_PROPERTY];
  return typeof own === 'string' && own !== '' ? own : null;
}

export interface WayfindingSignDestinationPatchInput {
  properties: Record<string, unknown>;
  /** Сколько сторон у типа указателя. */
  sides: number;
  /** Генератор типа; нет — наполнять нечего, пишется только сама точка. */
  generatorHash: string | null | undefined;
  /** Связи полей типа: ключ поля генератора → имя поля объекта. */
  links: Record<string, string> | undefined;
  /**
   * Поля генератора, которые можно наполнять. `null` — схема ещё неизвестна: пишем все связи,
   * лишний ключ форма при следующей записи сама отбросит.
   */
  fillableKeys: ReadonlySet<string> | null;
  /** Новая конечная точка; `null` — снять. */
  destination: { objectId: string; properties: Record<string, unknown> } | null;
  /** Свойства объекта по id (для снятия: ссылки становятся ТЕКУЩИМ текстом объекта). */
  lookup: (objectId: string) => Record<string, unknown> | null | undefined;
}

/**
 * Правка свойств указателя при выборе конечной точки — одной записью, одним шагом отмены.
 *
 * Выбор кладёт ссылку на объект в каждое связанное поле каждой стороны — и в поле, которое
 * правили руками: новая точка — новые тексты. Обложка стороны помечается устаревшей, и форма
 * стороны пересобирает макет. Пустые данные ДРУГОГО генератора не наследуются — сторона
 * начинает с чистого листа. ✏️ 19.09 ЗАПОЛНЕННЫЕ данные другого генератора (точка из слоя
 * Автокрафта «Редактора», тип сменили) сторона не отдаёт: их судьбу решает человек плашкой
 * в панели указателя (`holdsForeignAutocraftData`), а не выбор конечной точки мимоходом.
 *
 * Снятие превращает ВСЕ ссылки в текст, который в них сейчас: человек снимает связь, чтобы
 * поправить надписи, а не стереть их. Текст равен тому, что уже собрано, — обложку не трогаем.
 */
export function wayfindingSignDestinationPatch(
  input: WayfindingSignDestinationPatchInput,
): Record<string, unknown> {
  const { properties, sides, generatorHash, links, fillableKeys, destination, lookup } = input;
  // `null` удаляет ключ: слияние свойств на сервере так снимает значение.
  const patch: Record<string, unknown> = {
    [WAYFINDING_SIGN_DESTINATION_PROPERTY]: destination?.objectId ?? null,
  };
  if (!generatorHash) {
    return patch;
  }
  for (let side = 1; side <= sides; side += 1) {
    const view = readWayfindingSignSideProperties(properties, side);
    if (holdsForeignAutocraftData(view, generatorHash)) {
      continue;
    }
    const foreign = view[AUTOCRAFT_GENERATOR_PROPERTY] !== generatorHash;
    const next: Record<string, unknown> = { ...(foreign ? null : readData(view[AUTOCRAFT_DATA_PROPERTY])) };
    let changed = false;

    if (destination) {
      for (const [fieldKey, column] of Object.entries(links ?? {})) {
        if (fillableKeys && !fillableKeys.has(fieldKey)) {
          continue;
        }
        const value = autocraftObjectRef(
          destination.objectId,
          stringifyPropertyValue(destination.properties[column]),
        );
        if (next[fieldKey] !== value) {
          next[fieldKey] = value;
          changed = true;
        }
      }
    } else {
      for (const [fieldKey, value] of Object.entries(next)) {
        const ref = readAutocraftObjectRef(value);
        if (!ref) {
          continue;
        }
        const column = links?.[fieldKey];
        const object = column ? lookup(ref.objectId) : null;
        next[fieldKey] = object ? stringifyPropertyValue(object[column!]) : ref.snapshot;
        changed = true;
      }
    }

    if (!changed) {
      continue;
    }
    Object.assign(
      patch,
      toWayfindingSignSidePatch(
        {
          [AUTOCRAFT_GENERATOR_PROPERTY]: generatorHash,
          [AUTOCRAFT_DATA_PROPERTY]: next,
          ...(destination ? { [AUTOCRAFT_COVER_SIGNATURE_PROPERTY]: AUTOCRAFT_COVER_SIGNATURE_OUTDATED } : {}),
          // Результат сборки прошлого генератора к новым данным не относится — как у формы.
          ...(foreign
            ? {
                [AUTOCRAFT_COVER_PROPERTY]: null,
                [AUTOCRAFT_PDF_PROPERTY]: null,
                [AUTOCRAFT_PAGE_PROPERTY]: AUTOCRAFT_DEFAULT_PREVIEW_PAGE,
                [AUTOCRAFT_PAGE_COUNT_PROPERTY]: null,
              }
            : {}),
        },
        side,
      ),
    );
  }
  return patch;
}

/**
 * Ссылки на объекты в свойствах КОПИИ фичи, переведённые на id копий (дубль карты, копия на
 * другой план). Возвращает только изменённые ключи верхнего уровня — их кладут поверх свойств;
 * `null` — менять нечего.
 *
 * @param mapId — id копии объекта; `undefined` — объект не копировался, ссылка остаётся на
 *   прежний (копия указателя на другом плане ведёт туда же, куда оригинал, и панель это скажет).
 */
export function remapWayfindingObjectLinks(
  properties: Record<string, unknown> | null | undefined,
  mapId: (objectId: string) => string | undefined,
): Record<string, unknown> | null {
  if (!properties) {
    return null;
  }
  const patch: Record<string, unknown> = {};
  const destination = properties[WAYFINDING_SIGN_DESTINATION_PROPERTY];
  const nextDestination = typeof destination === 'string' ? mapId(destination) : undefined;
  if (nextDestination) {
    patch[WAYFINDING_SIGN_DESTINATION_PROPERTY] = nextDestination;
  }
  for (let side = 1; side <= WAYFINDING_SIGN_MAX_SIDES; side += 1) {
    const key = wayfindingSignSideKey(AUTOCRAFT_DATA_PROPERTY, side);
    const data = readData(properties[key]);
    if (!data) {
      continue;
    }
    let next: Record<string, unknown> | null = null;
    for (const [fieldKey, value] of Object.entries(data)) {
      const ref = readAutocraftObjectRef(value);
      const mapped = ref ? mapId(ref.objectId) : undefined;
      if (ref && mapped) {
        next ??= { ...data };
        next[fieldKey] = autocraftObjectRef(mapped, ref.snapshot);
      }
    }
    if (next) {
      patch[key] = next;
    }
  }
  return Object.keys(patch).length > 0 ? patch : null;
}
